import { useRef, useState, useEffect } from "react";
import Loader from "../../../components/loader/loader";
import CreateFieldModal from "../../../components/field/CreateFieldModal";
import FieldsModal from "../../../components/field/FieldsModal";
import { api } from "../../../services/api";
import {
  MdAddCircle,
  MdArrowBack,
  MdDelete,
  MdDragHandle,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdSave,
} from "react-icons/md";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableFieldItem from "../../../components/field/SortableFieldItem";
import SortableSectionItem from "../../../components/field/SortableSectionItem";
import "./EditCustomization.css";

const formatDisplay = (name) => name.replace(/^hoa_/, "HOA_").replace(/^coa_/, "COA_").replace(/\brofr_/gi, "ROFR_").replace(/\brofr\b/gi, "ROFR").replace(/_/g, " ").split(" ").map((word) => ["HOA", "COA", "ROFR"].includes(word.toUpperCase()) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");

const sortFieldsByDisplayName = (fieldList) =>
  [...fieldList].sort((a, b) =>
    formatDisplay(a.field_name).localeCompare(formatDisplay(b.field_name), undefined, {
      sensitivity: "base",
    })
  );

const EditCustomization = ({ customizationId, onBack, onSuccess }) => {
  const errorRef = useRef(null);

  const [error, setError] = useState("");
  const [fields, setFields] = useState([]);
  const [sections, setSections] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [formName, setFormName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loaderText, setLoaderText] = useState("Loading...");
  const [loading, setLoading] = useState(true);
  const [showCreateFieldModal, setShowCreateFieldModal] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState("str");
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [originalTranslations, setOriginalTranslations] = useState([]);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletedSectionIds, setDeletedSectionIds] = useState([]);

  // New State for Collapsible Sidebar
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    const loadCustomization = async () => {
      setLoading(true);
      try {
        const customization = await api.get(`/customization/${customizationId}`);
        setFormName(customization.name);
        const translations = await api.get(`/translation/${customizationId}`);
        setOriginalTranslations(translations);
        const sectionData = await Promise.all(customization.sections.map(async (sectionId) => {
            const section = await api.get(`/section/full/${sectionId}`);
            return {
              id: String(section.id), name: section.name, collapsed: true,
              fields: section.fields.map((field) => {
                const translation = translations.find((t) => t.field_id === field.id && String(t.section_id) === String(section.id));
                return { ...field, displayName: translation?.translation || formatDisplay(field.field_name) };
              }),
            };
          }));
        setSections(sectionData);
        if (sectionData.length > 0) setActiveSectionId(sectionData[0].id);

        const [defaultFields, userFields] = await Promise.all([api.get("/fields"), fetchUserFields()]);
        const userFieldIds = userFields.map((f) => f.id);
        const merged = [...userFields, ...defaultFields.filter((f) => !userFieldIds.includes(f.id))];
        setFields(sortFieldsByDisplayName(merged));
      } catch (err) { setError("Failed to load customization."); } finally { setLoading(false); }
    };
    loadCustomization();
  }, [customizationId]);

  const fetchUserFields = async () => {
    try {
      const userFields = await api.get("/fields/user-created");
      return userFields.map((f) => ({ ...f, isUserCreated: true }));
    } catch (err) { return []; }
  };

  const activateSection = (sectionId) => {
    setActiveSectionId(sectionId);
    setSections((prev) =>
      prev.map((section) => ({
        ...section,
        collapsed: sectionId ? section.id !== sectionId : true,
      }))
    );
  };

  const addNewSection = () => {
    const newSectionId = crypto.randomUUID().toString();
    setActiveSectionId(newSectionId);
    setSections((prev) => [
      ...prev.map((section) => ({ ...section, collapsed: true })),
      { id: newSectionId, name: "New Section", collapsed: false, fields: [] },
    ]);
  };

  const handleSave = async () => {
    try {
      if (!formName.trim()) return setError("Customization name is required.");
      setLoaderText("Editing customization...");
      setLoading(true);

      if (deletedSectionIds.length > 0) {
        await Promise.all(deletedSectionIds.map(async (id) => { try { await api.delete(`/section/${id}`); } catch (err) { console.error("Failed to delete section ID:", id, err); } }));
        setDeletedSectionIds([]);
      }

      const sectionIdMap = new Map();
      const newSections = sections.filter(s => isNaN(Number(s.id)));
      const existingSections = sections.filter(s => !isNaN(Number(s.id)));

      if (newSections.length > 0) {
        const payload = newSections.map(s => ({ name: s.name, fields: s.fields.map(f => f.id) }));
        const createdSectionIds = await api.post("/section", payload);
        newSections.forEach((section, index) => { sectionIdMap.set(section.id, String(createdSectionIds[index])); });
      }

      for (const section of existingSections) {
        await api.put(`/section/${section.id}`, { name: section.name, fields: section.fields.map((f) => f.id) });
        sectionIdMap.set(section.id, section.id);
      }

      const finalSectionIds = sections.map((s) => sectionIdMap.get(s.id) || s.id);
      await api.put(`/customization/${customizationId}`, { name: formName, sections: finalSectionIds });

      const changedExistingTranslations = [];
      const newTranslations = [];
      for (const section of sections) {
        const serverSectionId = sectionIdMap.get(section.id) || section.id;
        for (const f of section.fields) {
          const original = originalTranslations.find((t) => t.field_id === f.id && String(t.section_id) === String(serverSectionId));
          if (original) {
            if (f.displayName !== (original.translation || formatDisplay(f.field_name))) changedExistingTranslations.push({ id: original.id, translation: f.displayName });
          } else {
            newTranslations.push({ field_id: f.id, translation: f.displayName, section_id: serverSectionId });
          }
        }
      }

      if (changedExistingTranslations.length > 0) await api.put(`/translation/${customizationId}`, changedExistingTranslations);
      if (newTranslations.length > 0) await api.post(`/translation/${customizationId}`, newTranslations);
      onSuccess();
    } catch (err) { setError(`Error: ${err.message}`); } finally { setLoading(false); }
  };

  const handleBackClick = (e) => {
    e.preventDefault();
    if (window.confirm("Go back? Unsaved changes will be lost.")) onBack();
  };

  const toggleField = (field) => {
    if (!activeSectionId) return;
    setSections((prev) => prev.map((section) => {
        if (section.id !== activeSectionId) return section;
        const exists = section.fields.some((f) => f.id === field.id);
        const updatedFields = exists ? section.fields.filter((f) => f.id !== field.id) : [...section.fields, { ...field, displayName: formatDisplay(field.field_name) }];
        return { ...section, fields: updatedFields };
    }));
  };

  // Minimal implementation of handlers
  const handleSelectAll = () => { if (!activeSectionId) return; const filtered = sortFieldsByDisplayName(fields.filter((f) => f.field_name.toLowerCase().includes(searchTerm.toLowerCase()) || formatDisplay(f.field_name).toLowerCase().includes(searchTerm.toLowerCase()))); setSections((prev) => prev.map((s) => { if (s.id !== activeSectionId) return s; const existingIds = new Set(s.fields.map((f) => f.id)); const additions = filtered.filter((f) => !existingIds.has(f.id)).map((f) => ({ ...f, displayName: formatDisplay(f.field_name) })); return { ...s, fields: [...s.fields, ...additions] }; })); };
  const handleDeselectAll = () => { if (!activeSectionId) return; const filteredIds = new Set(fields.filter((f) => f.field_name.toLowerCase().includes(searchTerm.toLowerCase()) || formatDisplay(f.field_name).toLowerCase().includes(searchTerm.toLowerCase())).map((f) => f.id)); setSections((prev) => prev.map((s) => { if (s.id !== activeSectionId) return s; return { ...s, fields: s.fields.filter((f) => !filteredIds.has(f.id)) }; })); };
  const updateDisplayName = (sid, idx, val) => { setSections((prev) => prev.map((s) => s.id !== sid ? s : { ...s, fields: s.fields.map((f, i) => i === idx ? { ...f, displayName: val } : f) })); };
  const handleDragEnd = (e, sid) => { const { active, over } = e; if (!over || active.id === over.id) return; setSections((prev) => prev.map((s) => { if (s.id !== sid) return s; const oldI = s.fields.findIndex((f) => f.id === active.id); const newI = s.fields.findIndex((f) => f.id === over.id); const arr = [...s.fields]; const [mv] = arr.splice(oldI, 1); arr.splice(newI, 0, mv); return { ...s, fields: arr }; })); };
  const handleSectionDragEnd = (e) => { const { active, over } = e; if (!over || active.id === over.id) return; setSections((prev) => { const oldI = prev.findIndex((s) => s.id === active.id); const newI = prev.findIndex((s) => s.id === over.id); const arr = [...prev]; const [mv] = arr.splice(oldI, 1); arr.splice(newI, 0, mv); return arr; }); };
  const handleDeleteSection = (sid) => { const rm = sections.find(s => s.id === sid); if (rm && !isNaN(Number(rm.id))) setDeletedSectionIds(pi => [...pi, Number(rm.id)]); const ns = sections.filter(s => s.id !== sid); const nextActiveSectionId = activeSectionId === sid ? ns[0]?.id || null : activeSectionId; setActiveSectionId(nextActiveSectionId); setSections(ns.map(section => ({ ...section, collapsed: nextActiveSectionId ? section.id !== nextActiveSectionId : true }))); };
  const handleCreateField = async () => { try { if (!newFieldName.trim()) { setError("Field name is required."); return; } await api.post("/fields", [{ field_name: newFieldName, field_type: newFieldType === "list" ? "list[str]" : "str" }]); setNewFieldName(""); setNewFieldType("str"); setShowCreateFieldModal(false); const uf = await fetchUserFields(); const uids = new Set(uf.map((f) => f.id)); setFields((p) => sortFieldsByDisplayName([...uf, ...p.filter((f) => !uids.has(f.id))])); const ja = uf.find((f) => f.field_name === newFieldName); if (ja && activeSectionId) toggleField({ ...ja, displayName: formatDisplay(ja.field_name) }); } catch (err) { setError(`Error: ${err.message}`); } };
  const openCreateFieldModal = () => { setError(""); setNewFieldName(""); setNewFieldType("str"); setShowCreateFieldModal(true); };

  if (loading) return <Loader text={loaderText} />;
  const activeSection = sections.find((s) => s.id === activeSectionId);
  const activeSectionIndex = sections.findIndex((s) => s.id === activeSectionId);
  const totalFieldCount = sections.reduce((count, section) => count + section.fields.length, 0);
  const filteredFields = sortFieldsByDisplayName(fields.filter((f) =>
    f.field_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    formatDisplay(f.field_name).toLowerCase().includes(searchTerm.toLowerCase())
  ));

  return (
    <div className="customization-page customization-content-wrapper animate-fade-in">
        <div className="builder-header">
             <button className="secondary-button compact-btn back-button" onClick={handleBackClick}>
              <MdArrowBack /> Back
             </button>
             <div>
              <h1>Edit Customization</h1>
              <div className="compact-meta-row">
                <span>{sections.length} sections</span>
                <span>{totalFieldCount} fields</span>
                <span>Target: {activeSection ? `${activeSectionIndex + 1}. ${activeSection.name}` : "None"}</span>
              </div>
             </div>
        </div>

      <div ref={errorRef}>{error && <div className="error-banner">{error}</div>}</div>

      <div className="builder-command-bar">
        <div className="builder-name-control">
        <div className="input-group inline-name-group">
            <label className="input-label">Customization Name</label>
            <input type="text" className="text-input" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Enter Customization name..." style={{ borderColor: !formName.trim() && error ? "var(--brand-red)" : undefined }} />
        </div>
        </div>
        <div className="builder-action-group">
          <button type="button" className="secondary-button compact-btn" onClick={addNewSection}>
            <MdAddCircle /> New Section
          </button>
          <button className="primary-button compact-btn builder-save-button" onClick={handleSave}><MdSave /> Save</button>
        </div>
      </div>

      <CreateFieldModal show={showCreateFieldModal} onClose={() => setShowCreateFieldModal(false)} onCreate={handleCreateField} newFieldName={newFieldName} setNewFieldName={setNewFieldName} newFieldType={newFieldType} setNewFieldType={setNewFieldType} />
      {showFieldsModal && <FieldsModal token={localStorage.getItem("access_token")} onClose={() => setShowFieldsModal(false)} />}
      
      {showDeleteConfirm && (
        <div className="provider-overlay">
          <div className="provider-window delete-window">
             <div className="provider-body-scroll center-content">
                <p>Delete this section?</p>
                <div className="delete-actions">
                    <button className="primary-button danger-bg" onClick={() => { handleDeleteSection(sectionToDelete); setShowDeleteConfirm(false); setSectionToDelete(null); }}>Yes, Delete</button>
                    <button className="secondary-button" onClick={() => { setShowDeleteConfirm(false); setSectionToDelete(null); }}>Cancel</button>
                </div>
            </div>
          </div>
        </div>
      )}

      <div className="customization-layout">
        {/* Collapsible Left Panel */}
        <div className={`field-selector-container ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          <select className="select-input section-picker" value={activeSectionId || ""} onChange={(e) => activateSection(e.target.value !== "" ? e.target.value : null)}>
              <option value="">Select target section</option>
              {sections.map((section) => (<option key={section.id} value={section.id}>{section.name}</option>))}
          </select>
          <div className="field-selector-header" onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
            <div>
              <h3>Available Fields</h3>
            </div>
            <span className="collapse-icon">
                {isSidebarCollapsed ? <MdKeyboardArrowDown /> : <MdKeyboardArrowUp />}
            </span>
          </div>
          
          {!isSidebarCollapsed && (
             <div className="field-selector-content">
                <input type="text" className="text-input" placeholder="Search fields..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{marginBottom:'1rem'}} />
                <div className="field-pane-actions">
                    <button type="button" className="secondary-button compact-btn" onClick={openCreateFieldModal}>
                      <MdAddCircle /> Add Field
                    </button>
                    <button type="button" className="secondary-button compact-btn" onClick={handleSelectAll} disabled={!activeSectionId}>Select All</button>
                    <button type="button" className="secondary-button compact-btn" onClick={handleDeselectAll} disabled={!activeSectionId}>Deselect All</button>
                </div>
                
                {/* START OF CHANGED SECTION: Wrapped fields in fields-list-scroll */}
                <div className="fields-list-scroll">
                  {filteredFields.map((field) => {
                      const isChecked = !!activeSection?.fields.find((f) => f.id === field.id);
                      const isDisabled = !activeSectionId;
                      return (
                          <label key={field.id} className={`field-checkbox-label ${isChecked ? "is-selected" : ""} ${isDisabled ? "is-disabled" : ""}`}>
                          <input type="checkbox" checked={isChecked} onChange={() => toggleField(field)} disabled={isDisabled} />
                          <span className="field-name-text">{formatDisplay(field.field_name)}</span>
                          </label>
                      );
                  })}
                  {filteredFields.length === 0 && <div className="empty-section-placeholder">No fields found.</div>}
                </div>
                {/* END OF CHANGED SECTION */}

             </div>
          )}
        </div>

        <div className="sections-container">
          <DndContext collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
            <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              {sections.map((section) => (
                <SortableSectionItem key={section.id} id={section.id}>
                  {({ handleListeners }) => (
                    <div className={`field-section ${section.id === activeSectionId ? "active-section" : ""}`}>
                      <div className="field-section-header">
                        <MdDragHandle {...handleListeners} style={{ cursor: "grab", fontSize: "20px", color: "var(--text-secondary)" }} />
                        <input type="text" value={section.name} onChange={(e) => { const name = e.target.value; setSections((prev) => prev.map((s) => s.id === section.id ? { ...s, name } : s)); }} className="text-input no-drag" style={{ padding: "6px" }} />
                        <span className="section-field-count">{section.fields.length} fields</span>
                        <div className="section-actions-icons">
                          <button className={`target-section-btn no-drag ${section.id === activeSectionId ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); activateSection(section.id); }}>{section.id === activeSectionId ? "Editing" : "Edit"}</button>
                          <button className="section-toggle-btn no-drag" onClick={(e) => { e.stopPropagation(); setSections((prev) => prev.map((s) => s.id === section.id ? { ...s, collapsed: !s.collapsed } : s)); }}>{section.collapsed ? "View" : "Collapse"}</button>
                          <button type="button" className="delete-section-btn no-drag" aria-label={`Delete ${section.name}`} title="Delete section" onClick={(e) => { e.stopPropagation(); setSectionToDelete(section.id); setShowDeleteConfirm(true); }}><MdDelete /></button>
                        </div>
                      </div>
                      {!section.collapsed && (
                        <div className="field-section-content">
                          <DndContext collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, section.id)}>
                            <SortableContext items={section.fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                              {section.fields.map((field, index) => (
                                <SortableFieldItem key={field.id} id={field.id} field={field} index={index} updateDisplayName={(val) => updateDisplayName(section.id, index, val)} />
                              ))}
                              {section.fields.length === 0 && <div className="empty-section-placeholder">No fields in this section.</div>}
                            </SortableContext>
                          </DndContext>
                        </div>
                      )}
                    </div>
                  )}
                </SortableSectionItem>
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};
export default EditCustomization;
